import { liteClient as algoliasearch } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {
    chat,
    chatTrigger,
    clearRefinements,
    configure,
    hits,
    menu,
    pagination,
    panel,
    stats
} from 'instantsearch.js/es/widgets';
import { SearchIndexToolType } from 'instantsearch.js/es/widgets/chat/chat';
import { chatGreeting } from 'instantsearch.js/es/templates';
import { connectSearchBox } from 'instantsearch.js/es/connectors';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createRedirectUrlPlugin } from '@algolia/autocomplete-plugin-redirect-url';
import { history } from 'instantsearch.js/es/lib/routers';

/* eslint-disable */
const ALGOLIA_STATE_KEY = '__mkkAlgoliaSearch';

// Honesty-based detection: stealth scrapers spoofing a browser UA stay tagged
// HUMAN; the Algolia API logs (UA/IP) cover that gap.
const BOT_UA_PATTERN = /bot|crawler|spider|slurp|headless|lighthouse|prerender|phantomjs|selenium|puppeteer|playwright|scrapy|python-requests|curl|wget|semrush|ahrefs|screaming ?frog|dataforseo|petalbot|bytespider|gptbot|claudebot|perplexity|amazonbot|applebot|facebookexternalhit|bingpreview|googleother|inspectiontool|chatgpt|claude|meta-external/i;

const PROMPT_SUGGESTIONS = [
    'Welche Vorsorgeleistungen übernimmt die mkk?',
    'Wie kann ich eine Rechnung einreichen?',
    'Welche Leistungen bietet die mkk für Zahngesundheit?',
];

export default function AlgoliaInit()
{
    const SEARCH_PAGE_PATH = '/suche';
    const AUTOCOMPLETE_DEBOUNCE_MS = 500;
    const INSTANT_SEARCH_DEBOUNCE_MS = 900;
    const QUERY_SUGGESTIONS_DEBOUNCE_MS = 500;
    const MIN_QUERY_LENGTH = 3;
    const AUTOCOMPLETE_HITS_PER_PAGE = 5;
    const RESULTS_HITS_PER_PAGE = 10;
    const isMobile = window.matchMedia('(max-width: 899px)').matches;
    // "cubot" (Android phone brand) suppresses the whole UA pattern, not just
    // the "bot" token — real automation is still caught by navigator.webdriver.
    const trafficTag = (navigator.webdriver === true
        || (BOT_UA_PATTERN.test(navigator.userAgent) && !/cubot/i.test(navigator.userAgent)))
        ? 'BOT'
        : 'HUMAN';
    // Diagnostic: did the session open directly on a faceted URL (URL_FACETED —
    // bot/scraper/shared link) or land clean and refine via the UI (UI_FACETED)?
    const entryTag = /[?&](h\.|m\.|r\.|t\.)/.test(window.location.search)
        ? 'URL_FACETED'
        : 'UI_FACETED';
    const placeholder = isMobile
        ? 'Suche nach Themen, z.B. mkk App'
        : 'Suche nach Themen, z.B. Bonusprogramm, mkk App, ePA';
    const warnedMessages = {};

    const lifecycle = resetAlgoliaLifecycle();
    window[ALGOLIA_STATE_KEY] = lifecycle;

    const algoliaConfig = getAlgoliaConfig();

    if (!isValidAlgoliaConfig(algoliaConfig)) {
        warnAlgolia('Algolia search skipped: missing appId, apiKey or indexName configuration.');
        hideAskAiEntryPoints();
        lifecycle.destroy();
        delete window[ALGOLIA_STATE_KEY];
        return;
    }

    const indexName = algoliaConfig.indexName;
    const querySuggestionsIndexName = getQuerySuggestionsIndexName(algoliaConfig, indexName);
    let searchClient;

    try {
        searchClient = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey);
    } catch (error) {
        warnAlgolia('Algolia search skipped: search client could not be created.', error);
        hideAskAiEntryPoints();
        lifecycle.destroy();
        delete window[ALGOLIA_STATE_KEY];
        return;
    }

    const QUERY_SUGGESTIONS_ENDPOINT = '/algolia/query-suggestions';

    // Reused for the lifetime of the page so rapid focus/blur/retype cycles
    // don't re-hit even our own endpoint; the server owns the real 24h freshness.
    let cachedQuerySuggestions = null;

    /**
     * Fetches the shared server-cached popular-topics suggestions once per page
     * load; on failure clears the memo so a later call can retry and fall back.
     */
    function fetchCachedQuerySuggestions() {
        if (!cachedQuerySuggestions) {
            cachedQuerySuggestions = fetch(QUERY_SUGGESTIONS_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    appId: algoliaConfig.appId,
                    apiKey: algoliaConfig.apiKey,
                    indexName: querySuggestionsIndexName,
                }),
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Query suggestions endpoint returned ${response.status}`);
                    }

                    return response.json();
                })
                .catch((error) => {
                    cachedQuerySuggestions = null;
                    throw error;
                });
        }

        return cachedQuerySuggestions;
    }

    const EMPTY_SEARCH_ENDPOINT = '/algolia/empty-search';

    // Memoized per page load (keyed by the normalized batch) so clearing the query
    // or last refinement doesn't re-hit our endpoint; the server owns 24h freshness.
    let cachedEmptySearch = null;

    // Must match hasEntries in emptySearchCache.js, or qualifying batches get
    // rejected server-side and fall back to live Algolia.
    function hasFilterEntries(value) {
        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return value !== undefined && value !== null && value !== '';
    }

    /**
     * True only for the /suche landing state (empty query, first page, no
     * refinements); mirrors isEmptyLandingStateRequest in emptySearchCache.js.
     */
    function isEmptyLandingStateBatch(requests) {
        return Array.isArray(requests)
            && requests.length > 0
            && requests.every((request) => {
                const params = request?.params || {};

                return request?.indexName === indexName
                    && (params.query || '') === ''
                    && !params.page
                    && !hasFilterEntries(params.facetFilters)
                    && !hasFilterEntries(params.numericFilters)
                    && !hasFilterEntries(params.tagFilters)
                    && !hasFilterEntries(params.filters);
            });
    }

    /**
     * Normalizes the batch so all users share one cache entry: strip the per-user
     * token, disable clickAnalytics (shared queryID misattributes clicks), tag CACHE.
     */
    function normalizeEmptySearchBatch(requests) {
        return requests.map((request) => {
            const params = { ...request.params };

            delete params.userToken;
            params.analytics = true;
            params.clickAnalytics = false;
            params.analyticsTags = ['INSTANT_SEARCH', 'CACHE'];

            return { indexName: request.indexName, params };
        });
    }

    /**
     * Fetches the shared server-cached /suche landing state once per page load;
     * on failure clears the memo so a later call can retry and fall back.
     */
    function fetchCachedEmptySearch(requests) {
        const body = JSON.stringify({
            appId: algoliaConfig.appId,
            apiKey: algoliaConfig.apiKey,
            requests: normalizeEmptySearchBatch(requests),
        });

        if (!cachedEmptySearch || cachedEmptySearch.body !== body) {
            const promise = fetch(EMPTY_SEARCH_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Empty search endpoint returned ${response.status}`);
                    }

                    return response.json();
                })
                .catch((error) => {
                    // Only clear our own memo — a rejection arriving late
                    // must not discard a newer batch's cached promise.
                    if (cachedEmptySearch && cachedEmptySearch.body === body) {
                        cachedEmptySearch = null;
                    }

                    throw error;
                });

            cachedEmptySearch = { body, promise };
        }

        return cachedEmptySearch.promise;
    }

    /**
     * Results-page InstantSearch client: serves the /suche landing state from the
     * shared 24h cache, passing every real search (and cache failures) to Algolia.
     */
    const cachedInstantSearchClient = {
        ...searchClient,
        search(requests) {
            if (!isEmptyLandingStateBatch(requests)) {
                return searchClient.search(requests);
            }

            return fetchCachedEmptySearch(requests)
                .catch(() => searchClient.search(requests));
        },
    };

    /**
     * Builds a well-formed empty multi-query response (one result per request)
     * without calling Algolia, keeping the widget render lifecycle intact.
     */
    function emptySearchResults(requests) {
        return {
            results: (requests || []).map((request) => ({
                index: request?.indexName || indexName,
                hits: [],
                nbHits: 0,
                nbPages: 0,
                page: 0,
                hitsPerPage: 0,
                processingTimeMS: 0,
                exhaustiveNbHits: true,
                query: request?.params?.query || '',
                params: '',
            })),
        };
    }

    /**
     * True when every request has an empty query — the crawl abuse shape
     * (empty landing or empty-query facet enumeration), not a real text search.
     */
    function isEmptyQueryBatch(requests) {
        return Array.isArray(requests)
            && requests.length > 0
            && requests.every((request) => ((request?.params?.query || '') === ''));
    }

    /**
     * Bot-guard client (trafficTag BOT): only empty-query batches — the crawl
     * abuse — are kept off Algolia (landing state from the shared 24h cache,
     * other empty-query batches empty locally). Real text queries pass through,
     * so a misclassified human who actually searches still gets correct results;
     * bots almost never type a query, so the added quota is negligible.
     */
    const botStubSearchClient = {
        ...searchClient,
        search(requests) {
            if (!isEmptyQueryBatch(requests)) {
                return searchClient.search(requests);
            }

            if (isEmptyLandingStateBatch(requests)) {
                return fetchCachedEmptySearch(requests)
                    .catch(() => emptySearchResults(requests));
            }

            return Promise.resolve(emptySearchResults(requests));
        },
    };

    /**
     * Default Algolia params shared by autocomplete and InstantSearch.
     */
    const defaultAlgoliaSearchParams = {
        ruleContexts: ['enable-redirect-url'],
        clickAnalytics: true
    };

    let searchInstance = null;
    let chatInstance = null;
    let autocompleteInstance = null;
    let skipAutocompleteToInstantSearchUpdate = false;

    function createAlgoliaLifecycle() {
        return {
            autocompleteInstances: [],
            removeEventListeners: [],
            searchInstance: null,
            chatInstance: null,
            destroy() {
                this.removeEventListeners.forEach((removeEventListener) => removeEventListener());
                this.autocompleteInstances.forEach((instance) => {
                    if (instance?.destroy) {
                        instance.destroy();
                    }
                });

                if (this.searchInstance?.dispose) {
                    this.searchInstance.dispose();
                }

                if (this.chatInstance?.dispose) {
                    this.chatInstance.dispose();
                }

                this.autocompleteInstances = [];
                this.removeEventListeners = [];
                this.searchInstance = null;
                this.chatInstance = null;
            },
        };
    }

    /**
     * The markup ships the Ask-AI button visible; without a usable search
     * client it would be a dead control, so hide every copy of it.
     */
    function hideAskAiEntryPoints() {
        document.querySelectorAll('.search-bar__assistant').forEach((wrapper) => {
            wrapper.hidden = true;
        });
    }

    function resetAlgoliaLifecycle() {
        const previousState = window[ALGOLIA_STATE_KEY];

        if (previousState?.destroy) {
            previousState.destroy();
            delete window[ALGOLIA_STATE_KEY];
        }

        return createAlgoliaLifecycle();
    }

    function getAlgoliaConfig() {
        const config = window.algoliaConfig || {};

        return {
            appId: config.appId || (typeof algoliaApp !== 'undefined' ? algoliaApp : ''),
            apiKey: config.apiKey || (typeof algoliaApi !== 'undefined' ? algoliaApi : ''),
            indexName: config.indexName || (typeof algoliaIndex !== 'undefined' ? algoliaIndex : ''),
            querySuggestionsIndexName:
                config.querySuggestionsIndexName ||
                config.querySuggestionsIndex ||
                config.suggestionsIndexName ||
                '',
        };
    }

    function isValidAlgoliaConfig(config) {
        return Boolean(
            config.appId &&
            config.apiKey &&
            config.indexName
        );
    }

    function getQuerySuggestionsIndexName(config, indexName) {
        // TYPO3 can pass an environment-specific Query Suggestions index;
        return config.querySuggestionsIndexName || `${indexName}_query_suggestions`;
    }

    function isRollbarDebugMode() {
        try {
            return window.localStorage?.getItem('rollbar_debug') === 'true' ||
                window.location.search.includes('rollbar_debug=true');
        } catch (error) {
            return window.location.search.includes('rollbar_debug=true');
        }
    }

    function warnAlgolia(message, error = null, key = message) {
        if (warnedMessages[key]) return;

        warnedMessages[key] = true;

        if (window.rollbar?.warning) {
            try {
                window.rollbar.warning(message, {
                    error,
                    module: 'AlgoliaInit',
                });
                return;
            } catch (e) {
                // Fall back to debug console output below.
            }
        }

        if (isRollbarDebugMode() && window.console?.warn) {
            window.console.warn(message, error || '');
        }
    }

    function isSearchPage() {
        const pathname = window.location.pathname.replace(/\/+$/, '') || '/';

        return pathname === SEARCH_PAGE_PATH;
    }

    function debounce(fn, delay) {
        let timerId = undefined;

        return function debounced(...args) {
            if (timerId) {
                clearTimeout(timerId);
            }

            timerId = setTimeout(() => fn(...args), delay);
        };
    }

    function debounceGetItems(fn, delay) {
        let timer = undefined;

        return function debounced(...args) {
            if (timer) {
                clearTimeout(timer);
            }

            return new Promise((resolve, reject) => {
                timer = setTimeout(() => {
                    timer = undefined;
                    Promise.resolve(fn(...args)).then(resolve, reject);
                }, delay);
            });
        };
    }

    function isModifierEvent(event) {
        const isMiddleClick = event?.button === 1;

        return Boolean(
            isMiddleClick ||
            event?.altKey ||
            event?.ctrlKey ||
            event?.metaKey ||
            event?.shiftKey
        );
    }

    function getInstantSearchUiState() {
        if (!searchInstance) return {};

        const uiState = searchInstance.getUiState();

        return uiState[indexName] || {};
    }

    /**
     * Merges state into the active index and resets pagination to the first page.
     */
    function setInstantSearchUiState(indexUiState) {
        if (!searchInstance) return;

        searchInstance.setUiState((uiState) => ({
            ...uiState,
            [indexName]: {
                ...uiState[indexName],
                page: 1,
                ...indexUiState,
            },
        }));
    }

    /**
     * Debounced: mirror the autocomplete query into InstantSearch on /suche.
     */
    const debouncedSetInstantSearchQuery = debounce((query) => {
        if (!isSearchPage()) return;
        if (!searchInstance) return;

        // Skip low-value single-character queries, but always allow clearing back to empty.
        if (query.length > 0 && query.length < MIN_QUERY_LENGTH) return;

        const currentQuery = getInstantSearchUiState().query || '';

        if (currentQuery === query) return;

        // Autocomplete is the search box on /suche; updating InstantSearch
        // directly keeps suggestions and result hits on the same query.
        setInstantSearchUiState({ query });
    }, INSTANT_SEARCH_DEBOUNCE_MS);

    /**
     * Builds the /suche URL for the given UI state — via the router once
     * InstantSearch is up, or a plain search-query URL before then.
     */
    function getSearchPageUrl(indexUiState = {}) {
        if (!searchInstance) {
            const query = encodeURIComponent(indexUiState.query || '');
            return `${SEARCH_PAGE_PATH}${query ? `?search-query=${query}` : ''}`;
        }

        return searchInstance.createURL({
            [indexName]: {
                ...getInstantSearchUiState(),
                page: 1,
                ...indexUiState,
            },
        });
    }

    /**
     * Runs a selected suggestion: update InstantSearch in place on /suche,
     * else navigate to /suche with the query (modifier clicks pass through).
     */
    function runAutocompleteQuery({ query, setQuery, setIsOpen, event }) {
        const normalizedQuery = (query || '').trim();

        if (isModifierEvent(event)) {
            return;
        }

        event?.preventDefault?.();
        setQuery(normalizedQuery);
        setIsOpen(false);

        if (isSearchPage() && searchInstance) {
            setInstantSearchUiState({ query: normalizedQuery });
            return;
        }

        window.location.href = getSearchPageUrl({ query: normalizedQuery });
    }

    /**
     * True if any autocomplete collection has at least one item.
     */
    function hasAutocompletePanelItems(state) {
        return state.collections?.some((collection) => collection.items.length > 0) || false;
    }

    function getItemTitleAttribute(item) {
        if (item.title) return 'title';
        if (item.name) return 'name';
        return null;
    }

    /**
     * Renders a hit's title (highlight or snippet), '' if no title attribute.
     */
    function getItemTitle(hit, components, mode = 'highlight') {
        const attr = getItemTitleAttribute(hit);

        if (!attr) return '';

        return mode === 'highlight'
            ? components.Highlight({hit, attribute: attr})
            : components.Snippet({hit, attribute: attr});
    }


    /**
     * Picks the description attribute: introText, then description, else null.
     */
    function getItemDescriptionAttribute(hit) {
        if (hit.introText) return 'introText';
        if (hit.description) return 'description';
        return null;
    }

    /**
     * Parses a positive page number from the route, else undefined (default page).
     */
    function getRoutePage(route) {
        const page = Number(route.page);

        return Number.isFinite(page) && page > 0 ? page : undefined;
    }

    /**
     * Description content: highlighted text, a file fallback, or ''.
     */
    function getItemDescription(item, components, html) {
        const fallbackText = 'Das ist ein Dokument, das heruntergeladen werden kann.';
        const isFile = item.type === 'sys_file_metadata';
        const attr = getItemDescriptionAttribute(item);

        if (attr) {
            return components.Highlight({hit: item, attribute: attr});
        }

        return isFile ? html`${fallbackText}` : '';
    }

    /**
     * Editorial content type label (e.g. "Leistung", "News"), '' if the record has none.
     */
    function getItemContentType(item, html) {
        const contentType = typeof item.contentType === 'string' ? item.contentType.trim() : '';

        if (!contentType) return '';

        return html`<p class="search-content-type">${contentType}</p>`;
    }

    /**
     * File download icon for files, page icon otherwise.
     */
    function getItemIcon(item, html) {
        const isFile = item.type === 'sys_file_metadata';
        const iconKey = isFile ? 'download' : 'document';

        return html`<span class="type-icon" data-icon="${iconKey}"></span>`;
    }

    function getHitUrl(hit) {
        // TYPO3 file records can have a relative file URL or only a page fallback.
        if (hit.type === 'sys_file_metadata') {
            return hit.url
                ? (hit.url.startsWith('/') ? hit.url : '/' + hit.url)
                : '/?id=' + hit.pid;
        }

        return hit.url || (hit.type === 'pages' ? '/?id=' + hit.uid : '/?id=' + hit.pid);
    }

    /**
     * Appends insights params (s_oid/s_qid) so clicks/conversions attribute. See WEB-1350.
     */
    function appendInsightsParams(url, hit) {
        try {
            const u = new URL(url, window.location.origin);

            if (hit?.objectID) {
                u.searchParams.set('s_oid', hit.objectID);
            }

            // InstantSearch uses __queryID, Autocomplete uses __autocomplete_queryID.
            const queryId = hit?.__queryID ?? hit?.__autocomplete_queryID;

            if (queryId) {
                u.searchParams.set('s_qid', queryId);
            }

            return u.pathname + u.search + u.hash;
        } catch (e) {
            return url;
        }
    }

    function getRedirectUrl(results) {
        return results?.renderingContent?.redirect?.url;
    }

    function capitalizeQuery(query) {
        return query.charAt(0).toUpperCase() + query.slice(1);
    }

    const getDebouncedQuerySuggestionsItems = debounceGetItems((params, source) => {
        return source.getItems(params);
    }, QUERY_SUGGESTIONS_DEBOUNCE_MS);

    function getAskAiChatWidget(container) {
        return chat({
            container,
            agentId: '6c231d7e-57ff-49e4-b28c-9e9d0f1f7ceb',
            context: {
                locale: 'de',
            },
            resume: false,
            feedback: true,
            onError(error) {
                warnAlgolia('Algolia Ask-AI chat request failed.', error);
            },
            tools: {
                // The answer text is the result; the searched records are not shown.
                [SearchIndexToolType]: {
                    shouldRender: () => false,
                },
            },
            templates: {
                empty(props, {html}) {
                    // On mobile the suggestion block covers most of the viewport
                    // and pushes the prompt input out of sight, so it is desktop-only.
                    const promptSuggestionsBlock = isMobile ? '' : html`
                        <div class="ais-ChatPromptSuggestions">
                            <span class="ais-ChatPromptSuggestions-title">Beliebte Fragen</span>
                            ${PROMPT_SUGGESTIONS.map(
                                (prompt) => html`
                                    <button
                                        type="button"
                                        class="ais-Button ais-ChatPromptSuggestions-suggestion"
                                        onClick=${() =>
                                            props.sendMessage({text: prompt})}
                                    >
                                        <span class="ais-ChatPromptSuggestions-suggestionText">${prompt}</span>
                                        <svg class="ais-ChatPromptSuggestions-suggestionIcon" aria-hidden="true" focusable="false">
                                            <use href="#arrow-up-right"></use>
                                        </svg>
                                    </button>
                                `,
                            )}
                        </div>
                    `;

                    return html`
                        <div class="algolia-chat-intro">
                            <div class="algolia-chat-intro__head">
                                <span class="algolia-chat-avatar" aria-hidden="true">
                                    <svg class="algolia-chat-avatar__icon" focusable="false">
                                        <use href="#sparkles"></use>
                                    </svg>
                                </span>
                                ${chatGreeting({
                                    translations: {
                                        heading: 'Hallo! Wie kann ich dir helfen?',
                                        subheading:
                                            'Stell deine Frage – ich antworte auf Basis der Inhalte der mkk. Bitte gib keine persönlichen Daten oder Angaben zu deinem Einzelfall ein.',
                                    },
                                })(props)}
                            </div>

                            ${promptSuggestionsBlock}
                        </div>
                    `;
                },
                header: {
                    titleText: isMobile ? 'KI-Suchassistent' : 'KI-Suchassistent der mkk – meine krankenkasse',
                    clearLabelText: 'Chat leeren',
                    closeLabelText: 'Schließen',
                    maximizeLabelText: 'Vergrößern',
                    minimizeLabelText: 'Verkleinern',
                },
                messages: {
                    copyToClipboardLabelText: 'Antwort kopieren',
                    regenerateLabelText: 'Antwort neu erstellen',
                    scrollToBottomLabelText: 'Zum Ende springen',
                    error({ onNewConversation }, { html }) {
                        return html`
                            <span>
                                Entschuldigung, im Moment kann keine Antwort erstellt werden.
                                Bitte versuche es später erneut.
                            </span>
                            <button
                                type="button"
                                class="ais-Button ais-Button--primary ais-Button--md ais-ChatMessage-errorAction"
                                onClick=${() => onNewConversation()}
                            >
                                Neue Unterhaltung starten
                            </button>
                        `;
                    },
                },
                // Sparkle avatar so every assistant answer is clearly identifiable as AI.
                assistantMessage: {
                    leading(_, { html }) {
                        return html`
                            <span class="algolia-chat-avatar" aria-hidden="true">
                                <svg class="algolia-chat-avatar__icon" focusable="false">
                                    <use href="#sparkles"></use>
                                </svg>
                            </span>
                        `;
                    },
                },
                // Custom "thinking" state: status + animated dots + skeleton so the
                // wait reads as "an answer is forming", not just a spinner. No avatar
                // here — the assistant message that follows carries the avatar, so a
                // loader avatar would duplicate it during the fetch phase.
                loader(_, { html }) {
                    return html`
                        <div class="algolia-chat-thinking" role="status" aria-live="polite">
                            <span class="algolia-chat-thinking__status">
                                Ich durchsuche die Inhalte der mkk
                                <span class="algolia-chat-thinking__dots" aria-hidden="true">
                                    <span></span><span></span><span></span>
                                </span>
                            </span>
                            <div class="algolia-chat-thinking__skeleton" aria-hidden="true">
                                <span></span><span></span><span></span>
                            </div>
                        </div>
                    `;
                },
                loaderText: 'Antwort wird vorbereitet …',
                message: {
                    messageLabelText: 'Nachricht',
                    actionsLabelText: 'Aktionen für diese Nachricht',
                    reasoningLabelText: 'Gedankengang',
                    toolErrorRetryText: 'Erneut versuchen',
                },
                suggestions({ suggestions, onSuggestionClick }, { html }) {
                    const promptSuggestions = suggestions || [];

                    if (promptSuggestions.length === 0) return null;

                    return html`
                        <div class="ais-ChatPromptSuggestions">
                            <span class="ais-ChatPromptSuggestions-title">Weitere Fragen</span>
                            ${promptSuggestions.map((suggestion) => html`
                                <button
                                    class="ais-Button ais-ChatPromptSuggestions-suggestion"
                                    type="button"
                                    onClick=${() => onSuggestionClick(suggestion)}
                                >
                                    <span class="ais-ChatPromptSuggestions-suggestionText">${suggestion}</span>
                                    <svg class="ais-ChatPromptSuggestions-suggestionIcon" aria-hidden="true" focusable="false">
                                        <use href="#arrow-up-right"></use>
                                    </svg>
                                </button>
                            `)}
                        </div>
                    `;
                },
                prompt: {
                    textareaLabelText: 'Frage eingeben',
                    textareaPlaceholderText: 'Stell eine Frage',
                    emptyMessageTooltipText: 'Bitte gib eine Frage ein',
                    sendMessageTooltipText: 'Frage senden',
                    stopResponseTooltipText: 'Antwort stoppen',
                    disclaimerText: 'KI kann Fehler machen. Bitte prüfe wichtige Informationen.',
                },
            },
        });
    }

    const getDebouncedAlgoliaResults = debounceGetItems((query) => {
        return getAlgoliaResults({
            searchClient,
            queries: [
                {
                    indexName,
                    params: {
                        query,
                        ...defaultAlgoliaSearchParams,
                        hitsPerPage: AUTOCOMPLETE_HITS_PER_PAGE,
                        analyticsTags: [
                            'AUTOCOMPLETE',
                            ...(isMobile ? ['MOBILE', 'AUTOCOMPLETE_MOBILE'] : ['DESKTOP', 'AUTOCOMPLETE_DESKTOP']),
                            trafficTag,
                        ]
                    },
                },
            ],
        });
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    // Query suggestions (left column) for autocomplete.
    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
        searchClient,
        indexName: querySuggestionsIndexName,
        getSearchParams({ state }) {
            return {
                hitsPerPage: isMobile ? 3 : state.query ? 5 : 7,
                analyticsTags: [
                    'QUERY_SUGGESTIONS_AUTOCOMPLETE',
                    ...(isMobile ? ['MOBILE'] : ['DESKTOP']),
                    trafficTag,
                ],
            };
        },
        transformSource({ source }) {
            return {
                ...source,
                sourceId: 'querySuggestions',
                getItems(params) {
                    const query = params.state.query.trim();

                    if (query.length < MIN_QUERY_LENGTH) {
                        // Short/empty queries are identical for everyone and change
                        // ≤ once/day, so serve them from the shared 24h cache.
                        return fetchCachedQuerySuggestions()
                            .then((payload) => {
                                const hits = Array.isArray(payload?.hits) ? payload.hits : [];
                                const hitsPerPage = isMobile ? 3 : 7;

                                return hits.slice(0, hitsPerPage);
                            })
                            .catch(() => source.getItems(params));
                    }

                    // Hide the QS on /suche, as real hits are more relevant.
                    if (isSearchPage()) {
                        return [];
                    }

                    return getDebouncedQuerySuggestionsItems(params, source);
                },
                getItemUrl({ item }) {
                    return getSearchPageUrl({ query: item.query });
                },
                onSelect({ item, setQuery, setIsOpen, event }) {
                    runAutocompleteQuery({
                        query: item.query,
                        setQuery,
                        setIsOpen,
                        event,
                    });
                },
                templates: {
                    ...source.templates,
                    header() {
                        return 'Häufig nachgefragte Themen';
                    },
                },
            };
        },
    });

    // Redirect URL plugin (right column). Redirects are configured in the Algolia dashboard.
    const redirectUrlPlugin = createRedirectUrlPlugin({
        getSearchParams() {
            return {ruleContexts: ['enable-redirect-url']};
        },
        templates: {
            header({html, items}) {
                if (!items || items.length === 0) return null;
                return html`<div class="aa-SourceHeader">Schnellzugriff</div>`;
            },
            item({state, html}) {
                return html`
                    <div class="aa-ItemContent aa-HitItem">
                        <div class="aa-ItemIcon aa-ItemIconHits" aria-hidden="true">
                            <span class="type-icon" data-icon="external-link"></span>
                        </div>
                        <div class="aa-ItemBody">
                            <div class="aa-ItemTitle aa-HitTitle">
                                <a class="aa-ItemLink">
                                    ${capitalizeQuery(state.query)}
                                </a>
                            </div>
                            <div class="aa-ItemDescription aa-HitDescription">
                                Direkt zur passenden Seite springen
                            </div>
                        </div>
                    </div>
                `;
            },
        },
        onRedirect(redirects, { navigator, state }) {
            // Take the first redirect entry and grabs its first URL. Abort if missing.
            const item = redirects[0];
            const itemUrl = item?.urls?.[0];

            if (!itemUrl) return;

            // Always open redirect URLs in a new tab.
            navigator.navigateNewTab({ itemUrl, item, state });
        },
    });

    function initializeAlgoliaAutocomplete(containerId, prefix = null) {
        if (!document.getElementById(containerId)) return null;

        // Prefill search input from URL query parameter (if any).
        const params = new URLSearchParams(window.location.search);
        const initialQuery = isSearchPage()
            ? getInstantSearchUiState().query || params.get('search-query') || ''
            : params.get('search-query') || '';

        const options = {
            container: `#${containerId}`,
            placeholder: placeholder,
            openOnFocus: true,
            autoFocus: false,
            insights: true,
            shouldPanelOpen({ state }) {
                // On /suche, autocomplete only renders plugin sources; hide the
                // panel when those sources have no items instead of showing an empty box.
                if (isSearchPage()) {
                    return hasAutocompletePanelItems(state);
                }

                return true;
            },
            initialState: { query: initialQuery },
            plugins: [querySuggestionsPlugin, redirectUrlPlugin],
            detachedMediaQuery: 'none',
            translations: {
                clearButtonTitle: 'Leeren',
                detachedCancelButtonText: 'Abbrechen',
                submitButtonTitle: 'Absenden',
            },
            classNames: {
                form: prefix === 'mobile' ? 'header-search-form' : '',
                input: prefix === 'mobile' ? 'header-search-form__input' : '',
                submit: prefix === 'mobile' ? 'header-search-form__btn' : '',
            },
            onSubmit({ state }) {
                // On enter, redirect to /suche with the query.
                const query = (state.query || '').trim();


                if (isSearchPage() && searchInstance) {
                    setInstantSearchUiState({ query });
                    return;
                }

                window.location.href = getSearchPageUrl({ query });
            },
            onReset() {
                if (isSearchPage() && searchInstance) {
                    setInstantSearchUiState({
                        query: '',
                        menu: {},
                        refinementList: {},
                        toggle: {},
                    });
                }
            },
            onStateChange({ state, prevState }) {
                if (skipAutocompleteToInstantSearchUpdate) {
                    skipAutocompleteToInstantSearchUpdate = false;
                    return;
                }

                if (state.query !== prevState.query) {
                    debouncedSetInstantSearchQuery((state.query || '').trim());
                }
            },
            getSources() {
                if (isSearchPage()) {
                    return [];
                }

                return [
                    {
                        sourceId: 'hits',
                        getItems({ query }) {
                            const trimmedQuery = query.trim();

                            // Skip empty and low-value single-character queries.
                            if (trimmedQuery.length < MIN_QUERY_LENGTH) {
                                return [];
                            }

                            return getDebouncedAlgoliaResults(trimmedQuery);
                        },
                        getItemUrl({ item }) {
                            return appendInsightsParams(getHitUrl(item), item);
                        },
                        getItemInputValue({ item }) {
                            const attr = getItemTitleAttribute(item);

                            return attr ? item[attr] : '';
                        },
                        templates: {
                            header({ html, items, state }) {
                                if (!state.query.trim() || !items || items.length === 0) return null;

                                return html`Das kann dich interessieren`;
                            },
                            item({item, components, html}) {
                                return html`
                                    <div
                                        data-autocomplete-object-id="${item.objectID}"
                                        data-autocomplete-position="${item.__position}"
                                        data-autocomplete-query-id="${item.__autocomplete_queryID ?? item.__queryID ?? ''}">
                                        <div class="aa-ItemContent aa-HitItem">
                                            <div class="aa-ItemIcon aa-ItemIconHits" aria-hidden="true">
                                                ${getItemIcon(item, html)}
                                            </div>
                                            <div class="aa-ItemBody">
                                                <div class="aa-ItemTitle aa-HitTitle">
                                                    ${getItemTitle(item, components, 'snippet')}
                                                </div>
                                                <div class="aa-ItemDescription aa-HitDescription">
                                                    ${getItemDescription(item, components, html)}
                                                </div>
                                                ${getItemContentType(item, html)}
                                            </div>
                                        </div>
                                    </div>
                                `;
                            },
                            noResults({ html, state }) {
                                const redirects = (state?.context?.redirectUrlPlugin?.data ?? []);
                                const q = (state.query || '').trim();

                                if (!q) return null;
                                // We don't query below the minimum length, so don't claim "no results" either.
                                if (q.length < MIN_QUERY_LENGTH) return null;
                                if (redirects.length > 0) return null;

                                // Do not show "no results" while Autocomplete is still waiting
                                // for in-flight sources to finish.
                                if (state.status === 'loading' || state.status === 'stalled') {
                                    return null;
                                }

                                return html`
                                    <div class="aa-Empty">
                                        <span class="aa-EmptyIcon type-icon" data-icon="magnifying-glass-minus"></span>
                                        <div class="aa-EmptyContent">
                                            <div class="aa-EmptyTitle">Leider nichts gefunden</div>
                                            ${q ? html`<div class="aa-EmptyQuery">„${q}“</div>` : ''}
                                            <ul class="aa-EmptyTips">
                                                <li>Prüfe die Schreibweise.</li>
                                                <li>Verwende allgemeinere Begriffe.</li>
                                                <li>Versuche andere Suchwörter.</li>
                                            </ul>
                                        </div>
                                    </div>
                                `;
                            }
                        },
                        onSelect({item}) {
                            const targetUrl = appendInsightsParams(getHitUrl(item), item);

                            if (item.type === 'sys_file_metadata') {
                                window.open(targetUrl, '_blank', 'noopener');
                            } else {
                                window.location.href = targetUrl;
                            }
                        }
                    },
                ];
            },
        };

        autocompleteInstance = autocomplete(options);
        lifecycle.autocompleteInstances.push(autocompleteInstance);

        if (isSearchPage() && searchInstance) {
            autocompleteInstance.setQuery(getInstantSearchUiState().query || '');
            autocompleteInstance.refresh();
        }

        return autocompleteInstance;
    }

    /**
     * Stub for the chat's InstantSearch instance: the chat ignores search results,
     * but instantsearch fires an empty query on start() — answer it locally, free.
     */
    const chatStubSearchClient = {
        ...searchClient,
        search(requests) {
            return Promise.resolve(emptySearchResults(requests));
        },
    };

    /**
     * Creates and starts the Ask-AI chat (chat panel + trigger button) on the
     * user's first click.
     */
    function mountAskAiChat(container, triggerContainer) {
        if (chatInstance) return;

        chatInstance = instantsearch({
            indexName,
            searchClient: chatStubSearchClient,
            insights: true,
        });

        lifecycle.chatInstance = chatInstance;

        chatInstance.on('error', (event) => {
            warnAlgolia('Algolia Ask-AI chat failed.', event?.error || event);
        });

        chatInstance.addWidgets([
            getAskAiChatWidget(container),
            chatTrigger({
                container: triggerContainer,
                // Keep the button in its header position instead of the new
                // floating (fixed) default placement.
                floating: false,
                templates: {
                    icon: ({ isOpen }, { html }) => html`
                        <svg class="icon algolia-chat__icon" aria-hidden="true" focusable="false">
                            <use href="${isOpen ? '#x-mark' : '#sparkles'}"></use>
                        </svg>
                    `,
                },
            }),
        ]);

        chatInstance.start();

        // Continue the click that mounted the chat: open the panel (setOpen
        // also schedules the re-render that syncs the chatTrigger's icon).
        chatInstance.renderState[indexName]?.chat?.setOpen(true);
    }

    /**
     * Page-load setup for the Ask-AI entry point: wire the static button's
     * click to mountAskAiChat(); no Algolia objects created until then.
     */
    function initializeAskAiChat() {
        if (chatInstance) return;

        // The same partial is rendered in the header bar (mobile) and in the
        // main navigation (desktop); pick the copy by where it sits.
        const assistantWrapper = document.querySelector(
            isMobile ? '.header__bar__toggle .search-bar__assistant' : '.search-bar .search-bar__assistant'
        );
        const container = assistantWrapper?.querySelector('.algolia-chat');
        const openButton = assistantWrapper?.querySelector('.js-algolia-chat-open');
        const triggerContainer = assistantWrapper?.querySelector('.algolia-chat-trigger');

        if (!container || !openButton || !triggerContainer) return;

        // Re-reveal after a previous mount/teardown cycle hid it.
        openButton.hidden = false;

        const handleOpenClick = () => {
            // Hide (don't remove) the static button: the chatTrigger widget takes
            // over, and a later re-init can reveal it again.
            openButton.hidden = true;
            mountAskAiChat(container, triggerContainer);
        };

        openButton.addEventListener('click', handleOpenClick, { once: true });
        lifecycle.removeEventListeners.push(() => openButton.removeEventListener('click', handleOpenClick));
    }

    if (document.getElementById('algolia__instantsearch')) {
        searchInstance = instantsearch({
            indexName,
            searchClient: trafficTag === 'BOT' ? botStubSearchClient : cachedInstantSearchClient,
            insights: true,
            routing: {
                router: history({
                    window
                }),
                stateMapping: {
                    stateToRoute(uiState) {
                        const s = uiState[indexName] || {};
                        const route = {
                            'search-query': s.query || undefined,
                            page: s.page || undefined,
                            sortBy: s.sortBy || undefined,
                        };

                        // Hierarchical menu: one selected path per attribute
                        if (s.hierarchicalMenu) {
                            Object.entries(s.hierarchicalMenu).forEach(([attr, value]) => {
                                const v = Array.isArray(value)
                                    ? (value.length > 1 ? value.join(' > ') : value[0])
                                    : value;

                                if (v) route[`h.${attr}`] = v;
                            });
                        }

                        // Classic menu: one selected path per attribute
                        if (s.menu) {
                            Object.entries(s.menu).forEach(([attr, value]) => {
                                if (value) route[`m.${attr}`] = value;
                            });
                        }

                        // Refinement list: arrays -> repeated params
                        if (s.refinementList) {
                            Object.entries(s.refinementList).forEach(([attr, values]) => {
                                if (values && values.length) route[`r.${attr}`] = values;
                            });
                        }

                        // Toggle: boolean-ish
                        if (s.toggle) {
                            Object.entries(s.toggle).forEach(([attr, on]) => {
                                if (on) route[`t.${attr}`] = 1;
                            });
                        }

                        return route;
                    },
                    // URL -> UI state
                    routeToState(route) {
                        const next = {
                            query: route['search-query'] || '',
                            page: getRoutePage(route),
                        };

                        const hierarchicalMenu = {};
                        const menuState = {};
                        const refinementList = {};
                        const toggle = {};

                        Object.keys(route).forEach((key) => {
                            if (key === 'search-query' || key === 'page' || key === 'sortBy') return;

                            // h.<attribute>=<path>
                            if (key.startsWith('h.')) {
                                const attr = key.slice(2);
                                const val = route[key];
                                if (val) hierarchicalMenu[attr] = [val];
                                return;
                            }

                            // m.<attribute>=<value> (single; a repeated param keeps the last one)
                            if (key.startsWith('m.')) {
                                const attr = key.slice(2);
                                const raw = route[key];
                                const val = Array.isArray(raw) ? raw[raw.length - 1] : raw;
                                if (val) menuState[attr] = val;
                                return;
                            }

                            // r.<attribute>=value (can repeat; array or single)
                            if (key.startsWith('r.')) {
                                const attr = key.slice(2);
                                const val = route[key];
                                if (Array.isArray(val)) refinementList[attr] = val;
                                else if (val != null) refinementList[attr] = [val];
                                return;
                            }

                            // t.<attribute>=1  (truthy presence toggles on)
                            if (key.startsWith('t.')) {
                                const attr = key.slice(2);
                                if (route[key]) toggle[attr] = true;
                            }
                        });

                        if (Object.keys(hierarchicalMenu).length) next.hierarchicalMenu = hierarchicalMenu;
                        if (Object.keys(menuState).length) next.menu = menuState;
                        if (Object.keys(refinementList).length) next.refinementList = refinementList;
                        if (Object.keys(toggle).length) next.toggle = toggle;

                        if (route.sortBy) next.sortBy = route.sortBy;

                        return { [indexName]: next };
                    },
                },
            }
        });
        lifecycle.searchInstance = searchInstance;
        searchInstance.on('error', (event) => {
            warnAlgolia('Algolia instant search failed.', event?.error || event);
        });

        const hideablePagination = panel({
            hidden: ({ results }) => !results || results.nbPages <= 1,
        })(pagination);

        const contentTypeSection = [
            document.getElementById('accordion__label-content-type')?.closest('.accordion__heading'),
            document.getElementById('accordion__body-content-type'),
        ].filter(Boolean);

        const virtualSearchBox = connectSearchBox(() => {});
        const redirectContainer = document.getElementById('algolia__redirect');
        const redirectLink = redirectContainer?.querySelector('.js-algolia-redirect-link');
        const redirectTitle = redirectContainer?.querySelector('.js-algolia-redirect-title');
        const hasRedirectCard = Boolean(redirectContainer && redirectLink && redirectTitle);

        function resetRedirectCard() {
            redirectContainer.hidden = true;
            redirectLink.removeAttribute('href');
            redirectTitle.textContent = '';
        }

        const redirectCardWidget = {
            render({ results, status }) {
                // On error InstantSearch re-renders with the previous results; hide instead of showing a stale card.
                const redirectUrl = status === 'error' ? null : getRedirectUrl(results);

                if (!redirectUrl) {
                    resetRedirectCard();
                    return;
                }

                const query = (results.query || '').trim();
                const title = query ? capitalizeQuery(query) : 'Passende Seite';

                if (redirectLink.getAttribute('href') !== redirectUrl) redirectLink.href = redirectUrl;
                if (redirectTitle.textContent !== title) redirectTitle.textContent = title;
                redirectContainer.hidden = false;
            },
            dispose() {
                resetRedirectCard();
            },
        };

        // Hide "0 Ergebnisse" when the redirect card is the only thing shown for the query.
        const hideableStats = panel({
            hidden: ({ results }) => Boolean(
                hasRedirectCard && results && results.nbHits === 0 && getRedirectUrl(results)
            ),
        })(stats);

        searchInstance.addWidgets([
            virtualSearchBox({}),
            ...(hasRedirectCard ? [redirectCardWidget] : []),
            hideableStats({
                container: '#algolia__stats',
                templates: {
                    text(data, { html }) {
                        const count = data.nbHits.toLocaleString('de-DE');
                        const hasQuery = (data.query || '').trim().length > 0;
                        const noun = data.nbHits === 1 ? 'Ergebnis' : 'Ergebnisse';
                        const queryPart = hasQuery ? ` zum Suchwort „${data.query}“` : '';
                        return html`${count} ${noun}${queryPart}`;
                    },
                },
            }),
            hits({
                container: '#algolia__hits',
                templates: {
                    item: (hit, { html, components }) => {
                        const itemUrl = getHitUrl(hit);
                        const itemTarget = hit.type === 'sys_file_metadata' ? '_blank' : '';

                        // Append insights params to the final URL
                        const itemUrlWithParams = appendInsightsParams(itemUrl, hit);

                        return html`
                            <div
                                data-insights-object-id="${hit.objectID}"
                                data-insights-position="${hit.__position}"
                                data-insights-query-id="${hit.__queryID}">
                                <a href="${itemUrlWithParams}"
                                   class="search-result__wrapper"
                                   target="${itemTarget}">
                                    <div class="search-result__icon">
                                        ${getItemIcon(hit, html)}
                                    </div>
                                    <div class="search-result__info">
                                        <h3 class="p-h2 search-result__headline">
                                            ${getItemTitle(hit, components, 'highlight')}
                                        </h3>
                                        <p class="search-result__text">
                                            ${getItemDescription(hit, components, html)}
                                        </p>
                                        ${getItemContentType(hit, html)}
                                    </div>
                                </a>
                            </div>
                        `;
                    },
                    empty: (results, { html }) => {
                        if (hasRedirectCard && getRedirectUrl(results)) return null;

                        const { query } = results;

                        return html`
                            <div class="aa-Empty">
                                <span class="aa-EmptyIcon type-icon" data-icon="magnifying-glass-minus"></span>
                                <div class="aa-EmptyContent">
                                    <div class="aa-EmptyTitle">Leider nichts gefunden</div>
                                    ${query ? html`<div class="aa-EmptyQuery">„${query}“</div>` : ''}
                                    <ul class="aa-EmptyTips">
                                        <li>Prüfe die Schreibweise.</li>
                                        <li>Verwende allgemeinere Begriffe.</li>
                                        <li>Versuche andere Suchwörter.</li>
                                    </ul>
                                </div>
                            </div>
                        `;
                    },
                }
            }),
            configure({
                ...defaultAlgoliaSearchParams,
                hitsPerPage: RESULTS_HITS_PER_PAGE,
                analyticsTags: [
                    'INSTANT_SEARCH',
                    ...(isMobile ? ['MOBILE', 'INSTANT_SEARCH_MOBILE'] : ['DESKTOP', 'INSTANT_SEARCH_DESKTOP']),
                    trafficTag,
                    entryTag,
                ]
            }),
            clearRefinements({
                container: '#algolia__clear',
                templates: {
                    // We ignore hasRefinements inside because we hide disabled via CSS
                    resetLabel({hasRefinements}, {html}) {
                        return html`
                            <span class="type-icon clear-refinements-btn__icon" data-icon="funnel"></span>
                            <span class="clear-refinements-btn__label">Filter zurücksetzen</span>
                        `;
                    },
                },
                cssClasses: {
                    root: 'clear-refinements',
                    button: 'clear-refinements-btn',
                    disabledButton: 'clear-refinements-btn--disabled',
                },
            }),
            panel({
                hidden: ({ items }) => {
                    const isHidden = items.length === 0;
                    contentTypeSection.forEach((node) => { node.hidden = isHidden; });
                    return isHidden;
                },
            })(menu)({
                container: '#content-type-list',
                attribute: 'contentType',
                limit: 20,
                sortBy: ['name:asc'],
                templates: {
                    item({ label, count, url, cssClasses }, { html }) {
                        return html`<a class="${cssClasses.link}" href="${url}">
                            <span class="${cssClasses.label}">${label}</span>
                            <span class="${cssClasses.count}">${count.toLocaleString('de-DE')}</span>
                        </a>`;
                    },
                },
            }),
            panel()(menu)({
                container: '#type-list',
                attribute: 'type',
                sortBy: ['name:asc'],
                transformItems(items) {
                    return items.map((item) => {
                        let newLabel;
                        let iconKey;

                        // Map `type` to human-readable labels
                        if (item.label === 'pages') {
                            newLabel = 'Artikel';
                            iconKey = 'document';
                        } else if (item.label === 'sys_file_metadata') {
                            newLabel = 'Dokumente';
                            iconKey = 'download';
                        } else {
                            newLabel = item.label;
                            iconKey = 'newspaper';
                        }

                        return {
                            ...item,
                            label: newLabel,
                            iconKey
                        };
                    });
                },
                templates: {
                    item({ label, url, cssClasses, iconKey }, { html }) {
                        return html`<a class="${cssClasses.link}" href="${url}">
                            <span class="facet-label">
                                <span class="type-icon" data-icon="${iconKey}"></span>
                                <span class="${cssClasses.label}">${label}</span>
                            </span>
                        </a>`;
                    },
                },
            }),
            hideablePagination({
                container: '#algolia__pagination',
                cssClasses: {
                    list: ['pagination__list', 'u-list-style-none'],
                    selectedItem: 'pagination__entry--current',
                    link: ['pagination__entry', 'pagination__entry--linked'],
                },
            }),
        ]);

        let lastInstantSearchQuery = null;

        searchInstance.on('render', () => {
            if (!autocompleteInstance) return;

            const query = getInstantSearchUiState().query || '';

            if (query === lastInstantSearchQuery) return;

            lastInstantSearchQuery = query;
            skipAutocompleteToInstantSearchUpdate = true;
            autocompleteInstance.setQuery(query);
        });

        searchInstance.start();
    }

    initializeAskAiChat();

    if (isMobile) {
        initializeAlgoliaAutocomplete('algoliaAutocompleteMobile', 'mobile');
    } else {
        initializeAlgoliaAutocomplete('algoliaAutocomplete', 'desktop');
    }

    // Open search with Ctrl+K on Windows/Linux or Cmd+K on macOS, desktop only.
    if (!isMobile) {
        const handleSearchShortcut = (event) => {
            const isSearchShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';

            if (!isSearchShortcut) return;

            const target = event.target;
            const isTyping =
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement ||
                target?.isContentEditable;

            if (isTyping) return;

            event.preventDefault();

            // If the search bar is collapsed, open it by clicking the toggle.
            const searchToggle = document.querySelector('.search-bar__toggle');

            if (searchToggle && !searchToggle.classList.contains('js-search-form--open')) {
                searchToggle.click();
                return;
            }

            // Search bar is already open — focus the input directly.
            const container = document.getElementById('algoliaAutocomplete');
            const input = container?.querySelector('.aa-Input');

            if (input) {
                input.focus();
                input.select();
            }
        };

        document.addEventListener('keydown', handleSearchShortcut);
        lifecycle.removeEventListeners.push(() => document.removeEventListener('keydown', handleSearchShortcut));
    }
}
/* eslint-enable */
